import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
	Box,
	Button,
	Stack,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import { formatEntryLength } from "@/pages/sawlines/utils/entryBarcode";
import type { SawEntryQueueEntry } from "../repositories/sawEntryQueueRepository";
import {
	matchRowSx,
	WorkOrderMatchLabel,
} from "@/pages/sawlines/components/WorkOrderMatch";

// queue survives shifts: older scans carry the day
const scannedFormat = (at: string) =>
	dxtToLocalServerTime(at, "yyyy-MM-dd") ===
	dxtToLocalServerTime(new Date(), "yyyy-MM-dd")
		? "HH:mm"
		: "MMM d HH:mm";

const touchButton = { minWidth: 52, width: 52, height: 52, p: 0 };
const moveButton = { ...touchButton, borderColor: "divider" };

// 2695 · Gr 41 · 52' 0"
export const materialText = (
	section: string | null,
	grade: string | null,
	length: string | null,
) =>
	[section, grade && `Gr ${grade}`, length].filter(Boolean).join(" · ") || "—";

interface Props {
	entry: SawEntryQueueEntry;
	index: number;
	count: number;
	busy: boolean;
	onMove: (direction: -1 | 1) => void;
	onLoaded: () => void;
	onRemove: () => void;
}

export const MaterialQueueRow = ({
	entry,
	index,
	count,
	busy,
	onMove,
	onLoaded,
	onRemove,
}: Props) => (
	<TableRow sx={matchRowSx(entry.WorkOrderMatch)}>
		<TableCell>
			<Box
				sx={{
					width: 44,
					height: 44,
					borderRadius: 1,
					display: "grid",
					placeItems: "center",
					bgcolor: index === 0 ? "success.main" : "action.hover",
					color: index === 0 ? "success.contrastText" : "text.primary",
					fontSize: 22,
					fontWeight: 700,
				}}
			>
				{index + 1}
			</Box>
		</TableCell>
		<TableCell>
			<Typography sx={{ fontSize: 19, fontWeight: 700 }}>
				{entry.TagNo}
			</Typography>
			{entry.WorkOrderMatch === "current" ? (
				<Typography
					color="text.secondary"
					sx={{ fontSize: 13, whiteSpace: "nowrap" }}
				>
					{dxtToLocalServerTime(
						entry.ScannedAt,
						scannedFormat(entry.ScannedAt),
					)}
					{entry.ScannedByEmployee ? ` · #${entry.ScannedByEmployee}` : ""}
				</Typography>
			) : (
				<WorkOrderMatchLabel match={entry.WorkOrderMatch} size={13} />
			)}
		</TableCell>
		{/* specs stay on one line: section · grade · length never split */}
		<TableCell sx={{ whiteSpace: "nowrap" }}>
			<Typography sx={{ fontSize: 15 }}>
				{materialText(
					entry.SectionName,
					entry.GradeName,
					formatEntryLength(
						entry.MillLengthIn,
						entry.ScannedPartNumber != null,
					),
				)}
			</Typography>
			{entry.ScannedPartNumber && (
				<Typography color="text.secondary" sx={{ fontSize: 13 }}>
					Part {entry.ScannedPartNumber}
				</Typography>
			)}
			<Typography color="text.secondary" sx={{ fontSize: 13 }}>
				{entry.Pieces ?? "—"} pcs · Heat {entry.HeatNo ?? "—"}
			</Typography>
		</TableCell>
		<TableCell>
			<Stack direction="row" spacing={0.75} justifyContent="flex-end">
				{index === 0 && (
					<Button
						variant="contained"
						color="success"
						sx={{ minHeight: 52, px: 2, fontSize: 15, fontWeight: 700 }}
						disabled={busy}
						aria-label={`Load tag ${entry.TagNo} into the saw`}
						onClick={onLoaded}
					>
						Load
					</Button>
				)}
				<Button
					variant="outlined"
					color="inherit"
					sx={moveButton}
					disabled={busy || index === 0}
					aria-label={`Move tag ${entry.TagNo} up`}
					onClick={() => onMove(-1)}
				>
					<ArrowUpwardIcon />
				</Button>
				<Button
					variant="outlined"
					color="inherit"
					sx={moveButton}
					disabled={busy || index === count - 1}
					aria-label={`Move tag ${entry.TagNo} down`}
					onClick={() => onMove(1)}
				>
					<ArrowDownwardIcon />
				</Button>
				<Button
					variant="outlined"
					color="error"
					sx={touchButton}
					disabled={busy}
					aria-label={`Remove tag ${entry.TagNo}`}
					onClick={onRemove}
				>
					<DeleteOutlineIcon />
				</Button>
			</Stack>
		</TableCell>
	</TableRow>
);
